import React from "react";
import { Card, CardContent } from "../ui/card";
import { 
  Linkedin, 
  Twitter, 
  Facebook, 
  Video, 
  Mail, 
  Mic, 
  BarChart3,
  ArrowRight,
  Search
} from "lucide-react";

function OverviewTab({ generatedContent, setActiveTab }) {
  const stats = [
    {
      icon: Linkedin,
      label: "LinkedIn Carousel",
      count: `${generatedContent.linkedin?.length || 0} slides`,
      color: "#0077b5",
      tab: "linkedin"
    },
    {
      icon: Twitter,
      label: "Twitter Thread",
      count: `${generatedContent.twitter?.length || 0} tweets`,
      color: "#1DA1F2",
      tab: "twitter"
    },
    {
      icon: Facebook,
      label: "Facebook Posts",
      count: `${generatedContent.facebook?.length || 0} posts`,
      color: "#1877f2",
      tab: "facebook"
    },
    {
      icon: Video,
      label: "Video Scripts",
      count: `${generatedContent.videos?.length || 0} scripts`,
      color: "#FF0000",
      tab: "videos"
    },
    {
      icon: Mail,
      label: "Email Sequence",
      count: `${generatedContent.emails?.length || 0} emails`,
      color: "#EA4335",
      tab: "emails"
    },
    {
      icon: Mic,
      label: "Podcast Outline",
      count: `${generatedContent.podcast?.length || 0} sections`,
      color: "#9333ea",
      tab: "podcast"
    },
    {
      icon: BarChart3,
      label: "Infographic",
      count: `${generatedContent.infographic?.length || 0} sections`,
      color: "#00d4aa",
      tab: "infographic"
    },
    {
      icon: Search,
      label: "SEO Blogs",
      count: `${generatedContent.seoblogs?.length || 0} articles`,
      color: "#f59e0b",
      tab: "seoblogs"
    }
  ];

  const blogIdeas = generatedContent.blog_ideas || [];

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-4xl font-bold text-white">
          Your Content is Ready! 🎉
        </h2>
        <p className="text-xl text-white/60">
          Click any format below to view and copy your content
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={index}
              className="card cursor-pointer group"
              onClick={() => setActiveTab(stat.tab)}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: `${stat.color}20` }}
                    >
                      <Icon
                        className="w-6 h-6"
                        style={{ color: stat.color }}
                      />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{stat.label}</h3>
                      <p className="text-2xl font-bold" style={{ color: stat.color }}>
                        {stat.count}
                      </p>
                    </div>
                  </div>
                  <ArrowRight
                    className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Blog Ideas Preview */}
      {blogIdeas.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Blog Topic Ideas 💡</h3>
            <button
              onClick={() => setActiveTab("blog ideas")}
              className="text-[#00d4aa] hover:text-[#00e6bb] flex items-center gap-2 text-sm font-medium"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {blogIdeas.slice(0, 4).map((idea, index) => (
              <Card key={index} className="card hover:border-purple-500/30 transition-all cursor-pointer" onClick={() => setActiveTab("blog ideas")}>
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <span className="text-purple-400 font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold mb-1 line-clamp-1">{idea.title}</h4>
                      <p className="text-white/60 text-sm line-clamp-2">{idea.angle}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {blogIdeas.length > 4 && (
            <p className="text-center text-white/60 text-sm">
              +{blogIdeas.length - 4} more {blogIdeas.length - 4 === 1 ? 'idea' : 'ideas'} available
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 justify-center flex-wrap">
        <div className="text-center p-8 bg-white/5 rounded-2xl max-w-2xl">
          <h3 className="text-2xl font-bold text-white mb-4">
            What's Next?
          </h3>
          <p className="text-white/60 mb-6">
            Navigate through each tab to review your content. Click "Copy" buttons to grab the content, 
            or save everything to Airtable for later use.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => setActiveTab("linkedin")}
              className="px-6 py-3 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold rounded-lg transition-all"
            >
              Start with LinkedIn
            </button>
            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-lg transition-all"
            >
              Print Overview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OverviewTab;